import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Badge } from 'react-bootstrap';
import { PenSquare, Shield, AlertTriangle, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const UserEditModal = ({
                           user,
                           roles,
                           onUpdate,
                           selectedRole,
                           onRoleChange,
                           permissions = {},
                           disabled = false
                       }) => {
    const { t } = useTranslation('userManagement');
    const [show, setShow] = useState(false);
    const [editData, setEditData] = useState({
        is_active: user.is_active,
        role_id: null
    });
    const [confirmationRequired, setConfirmationRequired] = useState(false);
    const [confirmationText, setConfirmationText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Get the actual current role ID from the user object
    const getCurrentRoleId = () => {
        const roleId = selectedRole !== undefined ? selectedRole : (user.role?.id || user.role_id);
        return roleId ? parseInt(roleId) : null;
    };

    // Initialize editData when modal opens or user changes
    useEffect(() => {
        const currentRoleId = getCurrentRoleId();
        setEditData({
            is_active: user.is_active,
            role_id: currentRoleId
        });
    }, [user, selectedRole, show]);

    const handleClose = () => {
        setShow(false);
        setConfirmationRequired(false);
        setConfirmationText('');
        setIsSubmitting(false);

        const currentRoleId = getCurrentRoleId();
        setEditData({
            is_active: user.is_active,
            role_id: currentRoleId
        });
    };

    const handleShow = () => {
        if (!permissions.can_manage_users) {
            return;
        }

        const currentRoleId = getCurrentRoleId();
        setEditData({
            is_active: user.is_active,
            role_id: currentRoleId
        });
        setShow(true);
    };

    const isPrivilegedUser = () => {
        const userRole = roles.find(r => r.id === getCurrentRoleId());
        return ['HR', 'ADMIN', 'ICT', 'SECURITY', 'MANAGEMENT'].includes(userRole?.name);
    };

    const isStatusChangeAttempted = () => {
        return editData.is_active !== user.is_active;
    };

    const isRoleChangeAttempted = () => {
        const currentRoleId = getCurrentRoleId();
        const newRoleId = editData.role_id;
        return currentRoleId !== newRoleId;
    };

    const requiresConfirmation = () => {
        return isStatusChangeAttempted() || (isPrivilegedUser() && (isStatusChangeAttempted() || isRoleChangeAttempted()));
    };

    const getConfirmationMessage = () => {
        const currentRole = roles.find(r => r.id === getCurrentRoleId());
        const newRole = roles.find(r => r.id === editData.role_id);

        let messages = [];

        if (isStatusChangeAttempted()) {
            messages.push(`${t('form.status')}: ${editData.is_active ? t('status.active') : t('status.inactive')}`);
        }

        if (isRoleChangeAttempted()) {
            messages.push(`${t('form.role')}: ${currentRole?.name || 'None'} → ${newRole?.name || 'None'}`);
        }

        if (isPrivilegedUser()) {
            messages.push(`⚠️ ${t('role.privileged')} (${currentRole?.name})`);
        }

        return messages;
    };

    const handleSubmit = async () => {
        if (!permissions.can_manage_users || isSubmitting) {
            return;
        }

        if (requiresConfirmation() && !confirmationRequired) {
            setConfirmationRequired(true);
            return;
        }

        if (confirmationRequired && confirmationText.toLowerCase() !== 'confirm') {
            return;
        }

        setIsSubmitting(true);

        try {
            const updates = {};

            if (isStatusChangeAttempted()) {
                updates.is_active = editData.is_active;
            }

            if (isRoleChangeAttempted()) {
                if (editData.role_id === null || editData.role_id === '') {
                    alert(t('messages.error'));
                    setIsSubmitting(false);
                    return;
                }

                const roleId = parseInt(editData.role_id);
                if (isNaN(roleId) || roleId <= 0) {
                    alert(t('messages.error'));
                    setIsSubmitting(false);
                    return;
                }

                updates.role_id = roleId;
            }

            if (Object.keys(updates).length > 0) {
                await onUpdate(user.id, updates);

                if (updates.role_id) {
                    onRoleChange(user.id, updates.role_id);
                }
            }

            handleClose();
        } catch (error) {
            console.error('Submit failed:', error);
            setIsSubmitting(false);
        }
    };

    const handleConfirmationCancel = () => {
        setConfirmationRequired(false);
        setConfirmationText('');
    };

    const handleRoleSelectChange = (e) => {
        const value = e.target.value;
        const roleId = value === '' ? null : parseInt(value);

        setEditData(prev => ({
            ...prev,
            role_id: roleId
        }));
    };

    // Don't render if no permissions
    if (!permissions.can_manage_users) {
        return (
            <Button
                variant="outline-secondary"
                size="sm"
                className="p-1"
                disabled
                title={t('messages.noPermission')}
            >
                <Lock size={16} />
            </Button>
        );
    }

    return (
        <>
            <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleShow}
                className="p-1"
                disabled={disabled || isSubmitting}
                title={disabled ? t('messages.processing') : t('buttons.editUser')}
            >
                <PenSquare size={16} />
            </Button>

            <Modal show={show} onHide={handleClose} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('modal.editUserTitle')}: {user.first_name} {user.last_name}
                        {isPrivilegedUser() && (
                            <Badge bg="warning" className="ms-2" title={t('role.privileged')}>
                                <Shield size={12} className="me-1" />
                                {t('role.privileged')}
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!confirmationRequired ? (
                        <>
                            {/* FIXED: Conditional Permissions Alert */}
                            <Alert variant={permissions.can_manage_users ? "info" : "danger"} className="mb-3">
                                <Shield className="me-2" />
                                <strong>{t('permissions.title')}:</strong>
                                {permissions.can_manage_users ? (
                                    <>
                                        {t('permissions.manageUsers')} {permissions.can_reactivate_users && `| ${t('permissions.reactivateUsers')}`}
                                        {isPrivilegedUser() && (
                                            <div className="mt-1">
                                                <AlertTriangle size={16} className="me-1" />
                                                {t('modal.warningPrivileged')}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    t('messages.noPermission')
                                )}
                            </Alert>

                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('form.role')}</Form.Label>
                                    <Form.Select
                                        value={editData.role_id || ''}
                                        onChange={handleRoleSelectChange}
                                        disabled={disabled || isSubmitting}
                                    >
                                        <option value="">{t('form.selectRole')}</option>
                                        {roles.map(role => (
                                            <option key={role.id} value={role.id}>
                                                {role.name}
                                                {['HR', 'ADMIN', 'ICT', 'SECURITY', 'MANAGEMENT'].includes(role.name) && ` (${t('role.privileged')})`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        {t('messages.currentRole')}: {roles.find(r => r.id === getCurrentRoleId())?.name || 'None'}
                                        <br />
                                        {t('messages.selected')}: {roles.find(r => r.id === editData.role_id)?.name || 'None'}
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id={`active-switch-${user.id}`}
                                        label={t('form.activeUser')}
                                        checked={editData.is_active}
                                        onChange={(e) => setEditData(prev => ({
                                            ...prev,
                                            is_active: e.target.checked
                                        }))}
                                        disabled={disabled || isSubmitting}
                                    />
                                    <Form.Text className="text-muted">
                                        {t('messages.currentStatus')}: {user.is_active ? t('status.active') : t('status.inactive')}
                                        {user.status && ` (${user.status})`}
                                    </Form.Text>
                                </Form.Group>

                                {/* Show pending changes */}
                                {(isStatusChangeAttempted() || isRoleChangeAttempted()) && (
                                    <Alert variant="warning">
                                        <AlertTriangle className="me-2" />
                                        <strong>{t('messages.pendingChanges')}:</strong>
                                        <ul className="mb-0 mt-1">
                                            {getConfirmationMessage().map((msg, index) => (
                                                <li key={index}>{msg}</li>
                                            ))}
                                        </ul>
                                    </Alert>
                                )}

                                {/* User Information */}
                                <div className="border-top pt-3">
                                    <h6>{t('messages.userInfo')}</h6>
                                    <div className="row">
                                        <div className="col-sm-6">
                                            <strong>{t('form.username')}:</strong> {user.username}
                                        </div>
                                        <div className="col-sm-6">
                                            <strong>{t('form.email')}:</strong> {user.email}
                                        </div>
                                    </div>
                                    {user.last_status_change && (
                                        <div className="mt-2">
                                            <small className="text-muted">
                                                {t('messages.lastChanged')}: {new Date(user.last_status_change).toLocaleString()}
                                                {user.status_changed_by && ` ${t('common.by')} ${user.status_changed_by}`}
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </Form>
                        </>
                    ) : (
                        /* Confirmation Step */
                        <div>
                            <Alert variant="danger">
                                <AlertTriangle className="me-2" />
                                <strong>{t('messages.confirmationRequired')}</strong>
                            </Alert>

                            <div className="mb-3">
                                <h6>{t('modal.changesToMake')}</h6>
                                <ul>
                                    {getConfirmationMessage().map((msg, index) => (
                                        <li key={index}>{msg}</li>
                                    ))}
                                </ul>
                            </div>

                            <div className="mb-3">
                                <strong>{t('userDetail.name')}:</strong> {user.first_name} {user.last_name} ({user.username})
                            </div>

                            {isPrivilegedUser() && (
                                <Alert variant="warning">
                                    <Shield className="me-2" />
                                    <strong>{t('messages.confirmationRequired')}:</strong> {t('modal.warningPrivileged')}
                                </Alert>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    {t('form.typeConfirm')}:
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={confirmationText}
                                    onChange={(e) => setConfirmationText(e.target.value)}
                                    placeholder="Type 'confirm' here"
                                    className={confirmationText.toLowerCase() === 'confirm' ? 'is-valid' : ''}
                                    disabled={isSubmitting}
                                />
                            </Form.Group>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {!confirmationRequired ? (
                        <>
                            <Button
                                variant="secondary"
                                onClick={handleClose}
                                disabled={disabled || isSubmitting}
                            >
                                {t('buttons.cancel')}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={disabled || isSubmitting || (!isStatusChangeAttempted() && !isRoleChangeAttempted())}
                            >
                                {isSubmitting ? t('messages.saving') : (requiresConfirmation() ? t('buttons.reviewChanges') : t('buttons.saveChanges'))}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="secondary"
                                onClick={handleConfirmationCancel}
                                disabled={disabled || isSubmitting}
                            >
                                {t('buttons.back')}
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleSubmit}
                                disabled={disabled || isSubmitting || confirmationText.toLowerCase() !== 'confirm'}
                            >
                                <AlertTriangle className="me-1" />
                                {isSubmitting ? t('messages.processing') : t('buttons.confirmChanges')}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserEditModal;